// ============================================================================
// DETACHED MIRROR PIECE (Physics-based mirror fragment that flies off)
// ============================================================================

import CONFIG from '../config.js';
import Matter from 'matter-js';

const { Body } = Matter;

export class DetachedMirrorPiece {
    constructor(x, y, size, pieceIndex, physics = null, spawnForce = 200) {
        this.x = x;
        this.y = y;
        this.size = size;
        this.pieceIndex = pieceIndex; // 0, 1, or 2 for the three pieces
        this.physics = physics;
        this.isPickable = true;
        this.isCarried = false;
        this.carriedBy = null;
        this.rotation = 0;
        
        // Store the actual force/aggression (for game logic like damage calculation)
        // This is NOT capped - represents the true "intent"
        this.impactForce = spawnForce;

        // Random velocity with variance
        // Use configurable spawn speed multiplier
        const variance = 0.5 + Math.random() * CONFIG.throwableSpawnVariance;
        const baseSpeed = (spawnForce * CONFIG.throwableSpawnSpeedMultiplier * variance);

        // Random direction
        const angle = Math.random() * Math.PI * 2;
        const vx = Math.cos(angle) * baseSpeed;
        const vy = Math.sin(angle) * baseSpeed;

        // Random rotation speed (between min and max, not too fast)
        const rotationSpeed = CONFIG.throwableRotationSpeedMin + 
            Math.random() * (CONFIG.throwableRotationSpeedMax - CONFIG.throwableRotationSpeedMin);
        // Randomize direction
        const rotationDirection = Math.random() < 0.5 ? -1 : 1;
        const finalRotationSpeed = rotationSpeed * rotationDirection;
        
        // Create Matter.js body (dynamic circle)
        if (physics) {
            const { CATEGORY, MASK } = physics.constructor;
            const radius = size / 2;
            
            this.body = physics.createCircle(x, y, radius, {
                isStatic: false,
                frictionAir: CONFIG.throwableFrictionAir,  // Air friction (affects both linear and angular velocity)
                friction: CONFIG.environmentFriction,  // Increased surface friction for more realistic sliding
                restitution: CONFIG.wallBounceEnergyLoss,
                mass: 0.5,  // Light piece
                collisionFilter: {
                    category: CATEGORY.THROWABLE,
                    mask: MASK.THROWABLE  // Doesn't collide with player
                }
            });
            
            // Set initial velocity and rotation (let physics handle rotation after this)
            physics.setVelocity(this.body, vx, vy);
            physics.setAngle(this.body, Math.random() * Math.PI * 2);
            Body.setAngularVelocity(this.body, finalRotationSpeed);
            
            physics.linkEntity(this, this.body);
            
            // Create sensor body for pickup detection
            const sensorRadius = (CONFIG.pickupRadius || 40) * 0.8; // 20% smaller
            this.sensorBody = physics.createSensor(x, y, sensorRadius);
            physics.linkEntity(this, this.sensorBody);
        } else {
            this.body = null;
            this.sensorBody = null;
            this.vx = vx;
            this.vy = vy;
            this.rotationSpeed = rotationSpeed;
        }
    }
    
    update(dt, tilemap) {
        if (this.isCarried) return;
        
        if (this.body) {
            // Apply manual angular damping (Matter.js doesn't have built-in angularDamping)
            // angularDamping is the fraction remaining after 1 second, so we apply it per frame
            if (this.body.angularVelocity !== 0) {
                const angularDamping = CONFIG.throwableAngularDamping;
                Body.setAngularVelocity(this.body, this.body.angularVelocity * Math.pow(angularDamping, dt));
            }
            
            // Sync position from physics body
            this.x = this.body.position.x;
            this.y = this.body.position.y;
            this.rotation = this.body.angle;
            
            // Update sensor body position
            if (this.sensorBody && this.physics) {
                this.physics.setPosition(this.sensorBody, this.x, this.y);
            }
        } else {
            // Fallback to old system
            this.x += this.vx * dt;
            this.y += this.vy * dt;
            this.rotation += this.rotationSpeed * dt;
            
            // Apply friction
            this.vx *= CONFIG.environmentFriction;
            this.vy *= CONFIG.environmentFriction;
            this.rotationSpeed *= CONFIG.environmentFriction;
        }
    }

    isNearPlayer(player) {
        if (this.isCarried || !player || !player.body || !this.sensorBody || !this.physics) return false;
        // Use physics sensor to detect if player is in range
        return this.physics.isPlayerInSensor(this.sensorBody, player.body);
    }

    pickup(player) {
        if (!this.isPickable || this.isCarried) return false;
        
        this.isCarried = true;
        this.carriedBy = player;
        
        if (this.body && this.physics) {
            // Stop current motion
            this.physics.setVelocity(this.body, 0, 0);
            Body.setAngularVelocity(this.body, 0);
            
            // Attach to player using constraint
            if (player.body) {
                this.physics.attachToPlayer(player.body, this.body, 0, -CONFIG.playerSize - 8);
            }
        }
        
        return true;
    }

    drop() {
        if (!this.isCarried) return;
        
        this.isCarried = false;
        
        if (this.body && this.physics) {
            this.physics.detachFromPlayer(this.body);
        }
        
        this.carriedBy = null;
    }

    throwAt(targetX, targetY, throwForce) {
        if (!this.isCarried) return;
        
        // Store the actual throw force (for damage calculation)
        this.impactForce = throwForce;
        
        // Detach from player
        if (this.body && this.physics) {
            this.physics.detachFromPlayer(this.body);
        }
        
        this.isCarried = false;
        this.carriedBy = null;
        
        const dx = targetX - this.x;
        const dy = targetY - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist > 0) {
            // Use configurable throw speed multiplier
            const adjustedForce = throwForce * CONFIG.throwableThrowSpeedMultiplier;
            const vx = (dx / dist) * adjustedForce;
            const vy = (dy / dist) * adjustedForce;
            
            // Random rotation speed (between min and max, not too fast)
            const rotationSpeed = CONFIG.throwableRotationSpeedMin + 
                Math.random() * (CONFIG.throwableRotationSpeedMax - CONFIG.throwableRotationSpeedMin);
            const rotationDirection = Math.random() < 0.5 ? -1 : 1;
            const finalRotationSpeed = rotationSpeed * rotationDirection;
            
            if (this.body && this.physics) {
                this.physics.setVelocity(this.body, vx, vy);
                Body.setAngularVelocity(this.body, finalRotationSpeed);
            } else {
                this.vx = vx;
                this.vy = vy;
                this.rotationSpeed = finalRotationSpeed;
            }
        }
    }

    updateCarriedPosition(playerX, playerY) {
        // Handled by physics constraint now
        if (this.isCarried && !this.body) {
            this.x = playerX;
            this.y = playerY - CONFIG.playerSize - 5;
        }
    }

    update(dt, tilemap) {
        if (this.isCarried) return;
        
        // Sync position from physics body
        if (this.body) {
            this.x = this.body.position.x;
            this.y = this.body.position.y;
            this.rotation = this.body.angle;
            
            // Update sensor body position
            if (this.sensorBody && this.physics) {
                this.physics.setPosition(this.sensorBody, this.x, this.y);
            }
        } else {
            // Fallback physics
            // No gravity in top-down view
            this.vx *= CONFIG.environmentFriction;
            this.vy *= CONFIG.environmentFriction;
            this.rotationSpeed *= CONFIG.environmentFriction;

            const newX = this.x + this.vx * dt;
            const newY = this.y + this.vy * dt;

            const tile = tilemap.getTileAt(newX, newY);
            if (!tile || !tilemap.tileTypes[tile.type].walkable) {
                const tileX = tilemap.getTileAt(newX, this.y);
                const tileY = tilemap.getTileAt(this.x, newY);

                if (!tileX || !tilemap.tileTypes[tileX.type].walkable) {
                    this.vx = -this.vx * CONFIG.wallBounceEnergyLoss;
                }
                if (!tileY || !tilemap.tileTypes[tileY.type].walkable) {
                    this.vy = -this.vy * CONFIG.wallBounceEnergyLoss;
                }
            } else {
                this.x = newX;
                this.y = newY;
            }

            this.rotation += this.rotationSpeed * dt;
        }
    }

    draw(ctx, camera) {
        ctx.save();
        
        let drawX = this.x;
        let drawY = this.y;
        let drawRotation = this.rotation;
        
        if (this.body) {
            drawX = this.body.position.x;
            drawY = this.body.position.y;
            drawRotation = this.body.angle;
        }
        
        ctx.translate(drawX, drawY);
        ctx.rotate(drawRotation);

        // Draw mirror piece as a triangle (void-ish color)
        ctx.fillStyle = '#1a0033';
        ctx.strokeStyle = '#4a0066';
        ctx.lineWidth = 1;

        ctx.beginPath();
        const halfSize = this.size / 2;
        if (this.pieceIndex === 0) {
            ctx.moveTo(0, -halfSize);
            ctx.lineTo(-halfSize, halfSize);
            ctx.lineTo(halfSize, halfSize);
        } else if (this.pieceIndex === 1) {
            ctx.moveTo(-halfSize, 0);
            ctx.lineTo(halfSize, -halfSize);
            ctx.lineTo(halfSize, halfSize);
        } else {
            ctx.moveTo(halfSize, 0);
            ctx.lineTo(-halfSize, -halfSize);
            ctx.lineTo(-halfSize, halfSize);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // Add some reflective highlight
        ctx.fillStyle = 'rgba(200, 200, 255, 0.3)';
        ctx.fill();

        ctx.restore();
    }
}
